import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/fp/isEmpty';
import { STORE_BASE_PATH } from '../../common/constants';
import type { EditableField } from '../../common/types';
import {
    facadeApiV3,
    PlugType,
    TunnelCategory,
    VehicleProfilePlugTypes,
    type AssetDataResponse,
    type TruckType,
    type VehicleProfileDetails,
    type VehicleProfilePackWeight,
} from '../facade/facadeApiV3';
import { defaultVehicleProfile } from './defaultVehicleProfile';
import type { EmissionClass, HazardClass } from './vehicleProfileTypes';

export interface VehicleProfile {
    isProfileSubmited: boolean;
    currentBookingState?: string;
    defaultEvProfile?: string;
    assetLocked?: AssetDataResponse;
    isEV: EditableField<boolean>;
    profileType: EditableField<string>;
    truckType: EditableField<TruckType>;
    numberAttachedTrailers: number;
    profilePlugTypes: VehicleProfilePlugTypes[];
    trailer: EditableField<boolean>;
    length: EditableField<number>;
    height: EditableField<number>;
    width: EditableField<number>;
    weightPerAxle: EditableField<number>;
    weightPerAxleGroup: EditableField<number>;
    totalWeight: EditableField<number>;
    emptyWeight: EditableField<number>;
    cargoWeight: EditableField<number>;
    payloadWeight: EditableField<number>;
    vehicleAxles: EditableField<number>;
    trailerAxles: EditableField<number>;
    plugType: EditableField<PlugType>;
    batteryCapacity: EditableField<number>;
    batteryPacks: EditableField<number>;
    emissionClassification: EditableField<EmissionClass>;
    hazardClassification: EditableField<HazardClass>;
    tunnelCategory: EditableField<TunnelCategory>;
    vehiclePayload: EditableField<number>;
    averageOutdoorTemperature: EditableField<number>;
    minSoC: EditableField<number>;
    maxSoC: EditableField<number>;
    originSoC: EditableField<number>;
    destinationSoC: EditableField<number>;
    costPerKm: EditableField<number>;
    costPerKWh: EditableField<number>;
    maxCargoWeight: EditableField<number>;
    maxFreightTrainWeight: EditableField<number>;
    deviceId: EditableField<string>;
}

export const initialState: VehicleProfile = {
    isProfileSubmited: false,
    numberAttachedTrailers: 0,
    profilePlugTypes: [],
    isEV: { value: true, isEditable: true },
    profileType: { value: undefined, isEditable: true },
    truckType: { value: undefined, isEditable: true },
    trailer: { value: undefined, isEditable: true },
    length: { value: undefined, isEditable: true },
    height: { value: undefined, isEditable: true },
    width: { value: undefined, isEditable: true },
    totalWeight: { value: undefined, isEditable: true },
    emptyWeight: { value: undefined, isEditable: true },
    cargoWeight: { value: 0, isEditable: true },
    payloadWeight: { value: 0, isEditable: true },
    weightPerAxle: { value: undefined, isEditable: true },
    weightPerAxleGroup: { value: undefined, isEditable: true },
    vehicleAxles: { value: undefined, isEditable: true },
    trailerAxles: { value: undefined, isEditable: true },
    batteryCapacity: { value: undefined, isEditable: true },
    batteryPacks: { value: undefined, isEditable: true },
    emissionClassification: { value: undefined, isEditable: true },
    hazardClassification: { value: defaultVehicleProfile.hazardClassification, isEditable: true },
    tunnelCategory: { value: TunnelCategory.All, isEditable: true },
    vehiclePayload: { value: undefined, isEditable: true },
    averageOutdoorTemperature: { value: defaultVehicleProfile.averageOutdoorTemperature, isEditable: true },
    minSoC: { value: defaultVehicleProfile.minSoC, isEditable: true },
    maxSoC: { value: defaultVehicleProfile.maxSoC, isEditable: true },
    originSoC: { value: defaultVehicleProfile.originSoC, isEditable: true },
    destinationSoC: { value: defaultVehicleProfile.destinationSoC, isEditable: true },
    plugType: { value: undefined, isEditable: true },
    costPerKm: { value: defaultVehicleProfile.costPerKm, isEditable: true },
    costPerKWh: { value: defaultVehicleProfile.costPerKWh, isEditable: true },
    maxCargoWeight: { value: undefined, isEditable: false },
    maxFreightTrainWeight: { value: undefined, isEditable: false },
    deviceId: { value: '', isEditable: true },
};

export const vehicleProfileSlice = createSlice({
    name: `${STORE_BASE_PATH}vehicleProfile`,
    initialState,
    reducers: {
        clearVehicleProfileData: () => initialState,
        resetProfileDetails: state => {
            state.truckType = { value: undefined, isEditable: true };
            state.numberAttachedTrailers = 0;
            state.trailer = { value: undefined, isEditable: true };
            state.length = { value: undefined, isEditable: true };
            state.height = { value: undefined, isEditable: true };
            state.width = { value: undefined, isEditable: true };
            state.payloadWeight = { value: 0, isEditable: true };
            state.totalWeight = { value: undefined, isEditable: false };
            state.emptyWeight = { value: undefined, isEditable: true };
            state.cargoWeight = { value: undefined, isEditable: true };
            state.weightPerAxle = { value: undefined, isEditable: true };
            state.weightPerAxleGroup = { value: undefined, isEditable: true };
            state.vehicleAxles = { value: undefined, isEditable: true };
            state.trailerAxles = { value: undefined, isEditable: true };
            state.batteryCapacity = { value: 0, isEditable: true };
            state.batteryPacks = { value: undefined, isEditable: true };
        },
        changeIsProfileSubmited: (state, action) => {
            state.isProfileSubmited = action.payload;
        },
        changeCurrentBookingState: (state, action) => {
            state.currentBookingState = action.payload;
        },
        changeDefaultEvProfile: (state, action) => {
            state.defaultEvProfile = action.payload;
        },
        changeProfileDetails: (state, action: PayloadAction<VehicleProfileDetails>) => {
            const details = action.payload;

            state.numberAttachedTrailers = details.numberAttachedTrailers;
            state.profilePlugTypes = details.plugTypes;
            state.truckType = { value: details.truckType, isEditable: false };
            state.trailer = { value: details.numberAttachedTrailers > 0, isEditable: false };
            state.length = { value: details.length, isEditable: false };
            state.height = { value: details.height, isEditable: false };
            state.width = { value: details.width, isEditable: false };

            if (isEmpty(details.plugTypes)) {
                state.plugType = { value: PlugType.CcsCombo2, isEditable: false };
            } else if (details.plugTypes.includes(VehicleProfilePlugTypes.Ccs)) {
                state.plugType = { value: PlugType.CcsCombo2, isEditable: true };
            } else if (details.plugTypes.includes(VehicleProfilePlugTypes.Mcs)) {
                state.plugType = { value: PlugType.Mcs, isEditable: true };
            } else {
                state.plugType = { value: PlugType.CcsCombo2, isEditable: true };
            }

            state.payloadWeight = { value: 0, isEditable: false };

            state.maxCargoWeight.value = details.maxCargoWeight.find(
                (it: VehicleProfilePackWeight) => it.packs === details.defaultInstalledBatteryPackCount
            )?.weight;
            state.maxFreightTrainWeight.value = details.maxFreightTrainWeight;

            // Only set emptyWeight from profile if no vehicle is selected (curbWeight takes priority)
            if (!state.assetLocked?.details?.curbWeight) {
                const packWeight = details.emptyWeightVehicle.find(
                    (it: VehicleProfilePackWeight) => it.packs === details.defaultInstalledBatteryPackCount
                );
                if (packWeight) {
                    const packWeigthValue = packWeight.weight * 1000;
                    state.emptyWeight = { value: packWeigthValue, isEditable: false };
                    state.totalWeight = {
                        value: packWeigthValue + (state.cargoWeight.value ?? 0),
                        isEditable: false,
                    };
                }
            }
            // Only set batteryCapacity/batteryPacks from profile if not already set by asset data
            if (state.batteryCapacity.isEditable) {
                state.batteryCapacity.value = details.batteryCapacityPerPack * details.defaultInstalledBatteryPackCount;
                state.batteryPacks.value = details.defaultInstalledBatteryPackCount;
            } else {
                // Asset has locked batteryPacks — recalculate capacity using asset's pack count
                const assetBatteryPacks = state.batteryPacks.value;
                if (assetBatteryPacks) {
                    state.batteryCapacity.value = details.batteryCapacityPerPack * assetBatteryPacks;
                }
            }

            state.weightPerAxle = { value: details.maxWeightPerAxle * 1000, isEditable: false };
            const weightPerAxleGroup = details.maxWeightPerAxleGroup ? details.maxWeightPerAxleGroup * 1000 : undefined;
            state.weightPerAxleGroup = { value: weightPerAxleGroup, isEditable: false };
            state.vehicleAxles = { value: details.axlesVehicle, isEditable: false };
            state.trailerAxles = { value: details.axlesTrailer ?? 0, isEditable: false };
        },
        changeAssetLocked: (state, action: PayloadAction<AssetDataResponse | undefined>) => {
            state.assetLocked = action.payload;
            const curbWeight = action.payload?.details?.curbWeight;
            if (curbWeight !== undefined) {
                state.emptyWeight = { value: curbWeight, isEditable: false };
            }
            state.totalWeight = {
                value: (state.emptyWeight.value ?? 0) + (state.cargoWeight.value ?? 0),
                isEditable: false,
            };
            const batteryPacksNumber = action.payload?.batteryConfiguration?.batteryPacksNumber;
            if (batteryPacksNumber && batteryPacksNumber !== 0) {
                state.batteryCapacity.isEditable = false;
                state.batteryPacks.value = batteryPacksNumber;
            }
            const axlesNumber = action.payload?.axles?.[0]?.number;
            if (axlesNumber !== undefined) {
                state.vehicleAxles = { value: axlesNumber, isEditable: false };
            }
        },
        changeIsEV: (state, action) => {
            const isEV = action.payload;
            state.isEV = { value: isEV, isEditable: true };
            if (!isEV) {
                state.emissionClassification = {
                    value: defaultVehicleProfile.emissionClassification,
                    isEditable: true,
                };
            }
        },
        changeVehicleType: (state, action) => {
            if (!state.profileType.isEditable) {
                return;
            }
            state.profileType = { value: action.payload, isEditable: true };
        },
        lockVehicleType: (state, action: PayloadAction<string>) => {
            state.profileType = { value: action.payload, isEditable: false };
        },
        unlockVehicleType: state => {
            state.profileType = { ...state.profileType, isEditable: true };
        },
        changeTruckType: (state, action) => {
            state.truckType = { value: action.payload, isEditable: true };
        },
        changeTrailer: (state, action) => {
            state.trailer = { value: action.payload, isEditable: true };
        },
        changeLength: (state, action) => {
            state.length = { value: action.payload, isEditable: true };
        },
        changeHeight: (state, action) => {
            state.height = { value: action.payload, isEditable: true };
        },
        changeWidth: (state, action) => {
            state.width = { value: action.payload, isEditable: true };
        },
        changeTotalWeight: (state, action) => {
            state.totalWeight = { value: action.payload, isEditable: true };
        },
        changeEmptyWeight: (state, action) => {
            const emptyWeightValue = action.payload;
            state.emptyWeight.value = emptyWeightValue;
            state.totalWeight.value = emptyWeightValue + (state.cargoWeight.value ?? 0);
        },
        changeCargoWeight: (state, action) => {
            const cargoWeightValue = action.payload;
            state.cargoWeight.value = cargoWeightValue;
            state.totalWeight.value = cargoWeightValue + (state.emptyWeight.value ?? 0);
        },
        changeWeightPerAxle: (state, action) => {
            state.weightPerAxle.value = action.payload;
        },
        changeWeightPerAxleGroup: (state, action) => {
            state.weightPerAxleGroup.value = action.payload;
        },
        changeVehicleAxles: (state, action) => {
            state.vehicleAxles = { value: action.payload, isEditable: true };
        },
        changeTrailerAxles: (state, action) => {
            state.trailerAxles = { value: action.payload, isEditable: true };
        },
        changeBatteryCapacity: (state, action: PayloadAction<{ value: number; isEditable: boolean }>) => {
            state.batteryCapacity = action.payload;
        },
        changeBatteryPacksValue: (state, action: PayloadAction<number>) => {
            state.batteryPacks.value = action.payload;
        },
        changeBatteryPacks: (
            state,
            action: PayloadAction<{ profileDetails: VehicleProfileDetails | undefined; batteryPackCount: number }>
        ) => {
            const { batteryPackCount, profileDetails } = action.payload;

            // Only update batteryPacks if not locked by asset data
            if (state.batteryCapacity.isEditable) {
                state.batteryPacks.value = batteryPackCount;
            }

            // Only update emptyWeight from profile if no vehicle is selected (curbWeight takes priority)
            if (!state.assetLocked?.details?.curbWeight) {
                const packWeigth = profileDetails?.emptyWeightVehicle.find(
                    (it: VehicleProfilePackWeight) => it.packs === (state.batteryPacks.value ?? batteryPackCount)
                );
                if (packWeigth) {
                    const packWeigthValue = packWeigth.weight * 1000;
                    state.emptyWeight = { value: packWeigthValue, isEditable: false };
                    state.totalWeight = {
                        value: packWeigthValue + (state.cargoWeight.value ?? 0),
                        isEditable: false,
                    };
                }
            }
            // Only update capacity value if it's editable (not set by vehicle data)
            if (state.batteryCapacity.isEditable) {
                state.batteryCapacity.value = batteryPackCount * (profileDetails?.batteryCapacityPerPack ?? 0);
            }
        },
        changeEmissionClassification: (state, action) => {
            state.emissionClassification = { value: action.payload, isEditable: true };
        },
        changeHazardClassification: (state, action) => {
            state.hazardClassification = { value: action.payload, isEditable: true };
        },
        changeTunnelCategory: (state, action) => {
            state.tunnelCategory.value = action.payload;
        },
        changeVehiclePayload: (state, action) => {
            const payloadWeight = action.payload;
            state.payloadWeight.value = payloadWeight;
            state.totalWeight.value = (state.emptyWeight.value ?? 0) + payloadWeight;
        },
        changeAverageOutdoorTemperature: (state, action) => {
            state.averageOutdoorTemperature = { value: action.payload, isEditable: true };
        },
        changeMinSoC: (state, action) => {
            state.minSoC = { value: action.payload, isEditable: true };
        },
        changeMaxSoC: (state, action) => {
            state.maxSoC = { value: action.payload, isEditable: true };
        },
        changeOriginSoC: (state, action) => {
            state.originSoC = { value: action.payload, isEditable: true };
        },
        changeDestinationSoC: (state, action) => {
            state.destinationSoC = { value: action.payload, isEditable: true };
        },
        changePlugType: (state, action: PayloadAction<{ value: PlugType; isEditable: boolean }>) => {
            state.plugType = action.payload;
        },
        changeCostPerKm: (state, action) => {
            state.costPerKm = { value: action.payload, isEditable: true };
        },
        changeCostPerKWh: (state, action) => {
            state.costPerKWh = { value: action.payload, isEditable: true };
        },
        changeDeviceId: (state, action) => {
            state.deviceId = { value: action.payload, isEditable: true };
        },
    },
    extraReducers: builder => {
        builder.addMatcher(facadeApiV3.endpoints.getAssetSubscriptionState.matchFulfilled, (state, action) => {
            state.currentBookingState = action.payload.status;
        });
    },
});

export const {
    changeCurrentBookingState,
    changeDefaultEvProfile,
    resetProfileDetails,
    changeIsProfileSubmited,
    changeProfileDetails,
    changeAssetLocked,
    changeIsEV,
    changeVehicleType,
    lockVehicleType,
    unlockVehicleType,
    changeTruckType,
    changeTrailer,
    changeLength,
    changeHeight,
    changeWidth,
    changeTotalWeight,
    changeEmptyWeight,
    changeCargoWeight,
    changeWeightPerAxle,
    changeWeightPerAxleGroup,
    changeVehicleAxles,
    changeTrailerAxles,
    changeBatteryCapacity,
    changeBatteryPacksValue,
    changeBatteryPacks,
    changeEmissionClassification,
    changeHazardClassification,
    changeTunnelCategory,
    changeVehiclePayload,
    changeAverageOutdoorTemperature,
    changeMinSoC,
    changeMaxSoC,
    changeOriginSoC,
    changeDestinationSoC,
    changePlugType,
    changeCostPerKm,
    changeCostPerKWh,
    clearVehicleProfileData,
    changeDeviceId,
} = vehicleProfileSlice.actions;

export default vehicleProfileSlice.reducer;
